import { Box, Grid, Paper, Switch } from '@mui/material';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  ICalendarAccount,
  ICalendarEvent,
  IOutlookCalendarAccount,
  IOutlookCalendarEvent,
  ITask,
  NoteDTO,
  createCalendarEvent,
  createOutlookCalendarEvent,
  createTask,
  getCalendarAccounts,
  getOutlookCalendarAccounts,
  updateTask,
} from 'wisestep-api';
import {
  WsButton,
  WsButtonGroup,
  WsDateTimePicker,
  WsDialog,
  WsIconText,
  WsInputNumber,
  WsSelect,
  WsTypoColors,
  WsTypography,
  formattedDateISO,
} from 'wisestep-base';

import { useSKUModalStore } from '../../../../components/skuModal/SKUModalStore';
import { TaskFormValues } from '../../../../components/WsTaskCreationCard/TaskFormValues';
import { timeArr } from '../../../../components/WsTaskCreationCard/WsTaskCreationCard';
import { warning } from '../../../../utils/NotificationMessages';
import { INotification, useNotificationStore } from '../../../../utils/NotificationStore';

export interface WsNotesReminderProps {
  updateReminder?: boolean;
  calendarEventAccountGoogle?: ICalendarAccount;
  calendarEventAccountOutlook?: IOutlookCalendarAccount;
  loadCalendarFromParent?: boolean;
  closePopover: () => void;
  note: NoteDTO;
  setTaskId: (value: string) => void;
  task?: ITask;
}

const NotesReminder = ({
  updateReminder = false,
  calendarEventAccountGoogle,
  calendarEventAccountOutlook,
  loadCalendarFromParent,
  closePopover,
  note,
  setTaskId,
  task,
}: WsNotesReminderProps) => {
  const methods = useForm({
    defaultValues: {
      title: task?.title ?? '',
      timePeriod: task?.dueDate ?? formattedDateISO(dayjs().add(15, 'minute')['$d']),
      reminderIn: 'minutes',
      reminder: task?.remindBefore ?? 10,
      description: task?.description ?? '',
      recruiter: task?.assignedTo,
      sendEmail: true,
    },
  });
  const { handleSubmit, setValue } = methods;
  const setModal = useSKUModalStore((state) => state.setModal);
  const [open, setOpen] = useState(false);
  const [openGoogleFailure, setOpenGoogleFailure] = useState(false);
  const [openMicrosoftFailure, setOpenMicrosoftFailure] = useState(false);
  const [googleAccount, setGoogleAccount] = useState<ICalendarAccount | undefined>(
    calendarEventAccountGoogle
  );
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [googleCalender, setGoogleCalender] = useState(true);
  const [microsoftCalender, setMicrosoftCalender] = useState(false);
  const [outlookCalendarAccount, setOutlookCalendarAccount] = useState<
    IOutlookCalendarAccount | undefined
  >(calendarEventAccountOutlook);

  useEffect(() => {
    (async () => {
      const errorObj: INotification = {
        type: 'error',
        message: 'Error in fetching calendar accounts',
      };
      if (!loadCalendarFromParent) {
        try {
          const googleCalendarAcc = await getCalendarAccounts();
          if (googleCalendarAcc?.data && googleCalendarAcc.data.length > 0)
            setGoogleAccount(googleCalendarAcc.data[0] ?? null);
        } catch (e) {
          setNotification(errorObj);
        }
        try {
          const outlookCalendarAccount = await getOutlookCalendarAccounts();
          if (outlookCalendarAccount?.data && outlookCalendarAccount.data.length > 0)
            setOutlookCalendarAccount(outlookCalendarAccount.data[0] ?? null);
        } catch (e) {
          setNotification(errorObj);
        }
      }
    })();
  }, [loadCalendarFromParent, setNotification]);

  const sendReminder = (event: React.ChangeEvent<HTMLInputElement>) =>
    setValue('sendEmail', event.target.checked === true ? true : false);

  const checkGoogleCalender = async () => {
    if (!googleCalender && !googleAccount) setOpenGoogleFailure(!googleCalender);
    setGoogleCalender(!googleCalender);
  };

  const checkMicrosoftCalender = async () => {
    if (!microsoftCalender && !outlookCalendarAccount) setOpenMicrosoftFailure(!microsoftCalender);
    setMicrosoftCalender(!microsoftCalender);
  };

  useEffect(() => {
    const setGoogleOrOutlookAccount = () => {
      if (!isEmpty(googleAccount)) setGoogleCalender(true);
      if (!isEmpty(outlookCalendarAccount)) setMicrosoftCalender(true);
    };
    setGoogleOrOutlookAccount();
  }, [googleAccount, outlookCalendarAccount]);

  const createTimeHandler = (amount, unit) => {
    return dayjs().add(amount, unit).toISOString();
  };

  const addingTime = {
    '15mins': () => createTimeHandler(15, 'minute'),
    '30mins': () => createTimeHandler(30, 'minute'),
    '1Hr': () => createTimeHandler(1, 'hour'),
    '2Hr': () => createTimeHandler(2, 'hour'),
    '1Day': () => createTimeHandler(1, 'day'),
    '3Days': () => createTimeHandler(3, 'day'),
    '5Days': () => createTimeHandler(5, 'day'),
  };

  const selectedIndex = (index: number, value: string) => {
    if (value === 'custom') {
      setOpen(true);
    } else {
      const calculatedTimePeriod = addingTime[value]();
      setValue('timePeriod', calculatedTimePeriod);
      setOpen(false);
    }
  };

  const onFormSubmit = async (data) => {
    if (updateReminder && task?.status === 'OPEN') {
      const task: ITask = {
        comment: note.heading,
        remindBefore: data.reminderIn === 'minutes' ? data.reminder : data.reminder * 60,
        dueDate:
          data.customTime ?? data.timePeriod ?? formattedDateISO(dayjs().add(15, 'minute')['$d']),
        assignedTo: data.recruiter,
        description: note.description,
        entityType: 'notes',
        id: note.taskId,
      };
      try {
        const response = await updateTask(task);
        if (response.data) {
          setTaskId(response.data.id);
          addToGoogleCalender(data);
          addToMicrosoftCalender(data);
          setNotification({
            message: 'Task updated successfully.',
            type: 'success',
          });
          closePopover();
        }
      } catch (error: any) {
        if (error.response.status == 402) setModal({ object: error.response.data, type: 'count' });
        else setNotification(warning('Something went wrong, Please try again.'));
        closePopover();
      }
    } else {
      const task: ITask = {
        comment: note.heading,
        description: note.description,
        entityType: 'notes',
        sendEmail: data.sendEmail ?? true,
        remindBefore: data.reminderIn === 'minutes' ? data.reminder : data.reminder * 60,
        dueDate:
          data.customTime ?? data.timePeriod ?? formattedDateISO(dayjs().add(15, 'minute')['$d']),
      };
      try {
        const response = await createTask(task);
        if (response.data) {
          setTaskId(response.data.id);
          addToGoogleCalender(data);
          addToMicrosoftCalender(data);
          setNotification({
            message: 'Task created successfully.',
            type: 'success',
          });
          closePopover();
        }
      } catch (error: any) {
        if (error.response.status == 402) setModal({ object: error.response.data, type: 'count' });
        else setNotification(warning('Something went wrong, Please try again.'));
      }
    }
  };

  const addToGoogleCalender = async (data: TaskFormValues) => {
    if (googleCalender) {
      let reminder = data.reminder;
      if (data.reminderIn == 'hours') {
        reminder = data.reminder * 60;
      }
      const payload: ICalendarEvent = {
        attendees: data.recruiter ?? [],
        entityId: note?.id ?? '',
        entityType: 'notes',
        eventDescription: note.description,
        eventSummary: note.heading,
        from: data.customTime ?? data.timePeriod,
        to: dayjs(data.customTime ?? data.timePeriod)
          .add(15, 'minute')
          .toDate(),
        googleOAuth: googleAccount ?? null,
        location: data.location ?? '',
        reminders: [reminder],
      };
      await createCalendarEvent(payload);
    }
  };

  const addToMicrosoftCalender = async (data: TaskFormValues) => {
    if (microsoftCalender) {
      let reminder = data.reminder;
      if (data.reminderIn == 'hours') {
        reminder = data.reminder * 60;
      }
      const payload: IOutlookCalendarEvent = {
        attendees: data.recruiter,
        entityId: note?.id ?? '',
        entityType: 'notes',
        eventDescription: note.description,
        eventSummary: note.heading,
        from: data.customTime ?? data.timePeriod,
        to: dayjs(data.customTime ?? data.timePeriod)
          .add(15, 'minute')
          .toDate(),
        outlookOAuthToken: outlookCalendarAccount ?? null,
        location: data.location ?? '',
        reminders: [reminder],
      };
      await createOutlookCalendarEvent(payload);
    }
  };

  const redirectToGoogleCalenderSettings = () => (window.location.href = '/calender-settings');

  const redirectToMicrosoftCalenderSettings = () =>
    (window.location.href = '/calender-settings/microsoft');

  const closeGoogleFailure = () => {
    setOpenGoogleFailure(false);
    setGoogleCalender(false);
  };

  const closeMicrosoftFailure = () => {
    setOpenMicrosoftFailure(false);
    setMicrosoftCalender(false);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={2} xs={11.5} m={0}>
          <Grid item>
            <WsButtonGroup
              buttonsList={timeArr}
              inputTitle="Remind me in:*"
              selectedIndexValue={selectedIndex}
              defaultValue={0}
              size="small"
            />

            {open && (
              <Box mt={2}>
                <WsDateTimePicker name="customTime" label="Custom Date" disablePast={true} />
              </Box>
            )}
          </Grid>
          <Grid item>
            {(googleCalender || microsoftCalender) && (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <WsTypography bold>Notify me before</WsTypography>
                </Grid>
                <Grid item xs={7}>
                  <WsInputNumber name="reminder" required />
                </Grid>
                <Grid item xs={5}>
                  <WsSelect
                    placeholder="Minutes"
                    name="reminderIn"
                    selectArray={[
                      { label: 'Minutes', value: 'minutes' },
                      { label: 'Hours', value: 'hours' },
                    ]}
                    size="small"
                  />
                </Grid>
              </Grid>
            )}
          </Grid>
          <Grid container direction="column" spacing={2} xs={11.5} m={0} pl={1}>
            <Grid item>
              <WsIconText
                icon={<Switch onChange={sendReminder} defaultChecked />}
                text={'Notify by email'}
              />
            </Grid>
            <Grid item>
              <WsIconText
                icon={<Switch onChange={checkGoogleCalender} checked={googleCalender} />}
                text={'Add to Google Calender'}
              />
            </Grid>
            <Grid item>
              {googleAccount && googleCalender && (
                <WsTypography color={WsTypoColors.PRIMARY}>{googleAccount.email}</WsTypography>
              )}
              {!googleAccount && (
                <WsButton size="medium" variant="text" onClick={() => setOpenGoogleFailure(true)}>
                  Configure Now
                </WsButton>
              )}
            </Grid>
            <Grid item>
              <WsIconText
                icon={<Switch onChange={checkMicrosoftCalender} checked={microsoftCalender} />}
                text={'Add to Microsoft Calender'}
              />
            </Grid>
            <Grid item>
              {outlookCalendarAccount && microsoftCalender && (
                <WsTypography color={WsTypoColors.PRIMARY}>
                  {outlookCalendarAccount.email}
                </WsTypography>
              )}
              {!outlookCalendarAccount && (
                <WsButton
                  size="medium"
                  variant="text"
                  onClick={() => setOpenMicrosoftFailure(true)}
                >
                  Configure Now
                </WsButton>
              )}
            </Grid>
          </Grid>
          <Grid container spacing={2} direction="row" m={1} justifyContent="flex-end">
            <WsButton size="medium" variant="text" onClick={closePopover}>
              Close
            </WsButton>
            {updateReminder ? (
              <WsButton size="medium" variant="contained" type="submit">
                Update
              </WsButton>
            ) : (
              <WsButton size="medium" variant="contained" type="submit">
                Save
              </WsButton>
            )}
          </Grid>
          <WsDialog openDialog={openGoogleFailure}>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }} role="presentation">
              <Box py={1} pl={0.5}>
                <WsTypography>Please configure your email to set up a Google meeting.</WsTypography>
              </Box>
              <Box py={1} pl={0.5}>
                <WsButton
                  size="medium"
                  variant="contained"
                  onClick={redirectToGoogleCalenderSettings}
                >
                  Configure Now
                </WsButton>
                <WsButton size="medium" variant="text" onClick={closeGoogleFailure}>
                  Skip
                </WsButton>
              </Box>
            </Paper>
          </WsDialog>
          <WsDialog openDialog={openMicrosoftFailure}>
            <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }} role="presentation">
              <Box py={1} pl={0.5}>
                <WsTypography>
                  Please configure your email to set up a Microsoft meeting.
                </WsTypography>
              </Box>
              <Box py={1} pl={0.5}>
                <WsButton
                  size="medium"
                  variant="contained"
                  onClick={redirectToMicrosoftCalenderSettings}
                >
                  Configure Now
                </WsButton>
                <WsButton size="medium" variant="text" onClick={closeMicrosoftFailure}>
                  Skip
                </WsButton>
              </Box>
            </Paper>
          </WsDialog>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default NotesReminder;
